// ProgressiveCarousel - Auto-advancing carousel with GPU-accelerated CSS progress bars
// Uses transform: scaleX() transitions (compositor thread) instead of JS animation loops
import "./hero-splash.scss";
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";

function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

const ProgressSliderContext = createContext(undefined);

export const useProgressSliderContext = () => {
  const context = useContext(ProgressSliderContext);
  if (!context) {
    throw new Error(
      "useProgressSliderContext must be used within a ProgressSlider"
    );
  }
  return context;
};

export const ProgressSlider = ({
  children,
  duration = 5000,
  vertical = false,
  activeSlider,
  className,
}) => {
  const [active, setActive] = useState(activeSlider);
  const barRefs = useRef({});
  const sliderValuesRef = useRef([]);

  const registerBar = useCallback((value, el) => {
    if (el) barRefs.current[value] = el;
  }, []);

  // Extract slider values from children on mount
  useEffect(() => {
    const contentChild = React.Children.toArray(children).find(
      (child) => child.type === SliderContent
    );
    if (contentChild) {
      sliderValuesRef.current = React.Children.toArray(
        contentChild.props.children
      ).map((child) => child.props.value);
    }
  }, [children]);

  // Start CSS transition for active bar, listen for transitionend to auto-advance
  useEffect(() => {
    const el = barRefs.current[active];
    if (!el || sliderValuesRef.current.length === 0) return;

    const prop = vertical ? "scaleY" : "scaleX";

    // Reset all bars instantly
    Object.entries(barRefs.current).forEach(([key, barEl]) => {
      if (!barEl) return;
      barEl.style.transition = "none";
      barEl.style.transform = `${prop}(0)`;
    });

    // Force reflow so the reset applies before starting the transition
    void el.offsetHeight;

    // Start smooth GPU-accelerated transition on the active bar
    el.style.transition = `transform ${duration}ms linear`;
    el.style.transform = `${prop}(1)`;

    const handleTransitionEnd = (e) => {
      if (e.propertyName !== "transform") return;
      const values = sliderValuesRef.current;
      const idx = values.indexOf(active);
      const next = (idx + 1) % values.length;
      setActive(values[next]);
    };

    el.addEventListener("transitionend", handleTransitionEnd);
    return () => el.removeEventListener("transitionend", handleTransitionEnd);
  }, [active, duration, vertical]);

  const handleButtonClick = useCallback(
    (value) => {
      if (value !== active) setActive(value);
    },
    [active]
  );

  return (
    <ProgressSliderContext.Provider
      value={{ active, handleButtonClick, vertical, registerBar }}
    >
      <div className={cn("prog-carousel", className)}>{children}</div>
    </ProgressSliderContext.Provider>
  );
};

export const SliderContent = ({ children, className }) => {
  return (
    <div className={cn("prog-carousel__content", className)}>
      {React.Children.toArray(children).map((child) => {
        if (child.props.value === undefined) return child;
        return React.cloneElement(child, { key: child.props.value });
      })}
    </div>
  );
};

export const SliderWrapper = ({ children, value, className }) => {
  const { active } = useProgressSliderContext();
  const isActive = active === value;

  return (
    <div
      className={cn(
        "prog-carousel__slide",
        isActive ? "prog-carousel__slide--active" : "prog-carousel__slide--hidden",
        className
      )}
    >
      {children}
    </div>
  );
};

export const SliderBtnGroup = ({ children, className }) => {
  return (
    <div className={cn("prog-carousel__buttons", className)}>{children}</div>
  );
};

export const SliderBtn = ({ children, value, className }) => {
  const { active, handleButtonClick, registerBar } =
    useProgressSliderContext();

  return (
    <button
      className={cn(
        "prog-carousel__btn",
        active === value
          ? "prog-carousel__btn--active"
          : "prog-carousel__btn--inactive",
        className
      )}
      onClick={() => handleButtonClick(value)}
    >
      {children}
      <div className="prog-carousel__progress" role="progressbar">
        <span
          ref={(el) => registerBar(value, el)}
          className="prog-carousel__progress-fill"
        />
      </div>
    </button>
  );
};
